import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Key, ShieldCheck, TrendingUp, ChevronRight, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useAdVariant } from "@/context/AdVariantContext";
import { api } from "@/lib/api";
import Market2026Dialog from "@/components/market/Market2026Dialog";
import ConsultationForm from "@/components/forms/ConsultationForm";
import LoanForm from "@/components/forms/LoanForm";
import HandoverCollection from "@/components/handover/HandoverCollection";
import FAQ from "@/components/faq/FAQ";
import SourceRibbon from "@/components/market/SourceRibbon";
import ytdSnapshot from "@/data/ytd_snapshot.json";

export default function Home() {
  const { t, isRTL, lang, lh } = useLang();
  const { format } = useCurrency();
  const { variantId, variant } = useAdVariant();
  const loanRef = useRef();
  const [market2026Open, setMarket2026Open] = useState(false);
  const [latestPrint, setLatestPrint] = useState(null);
  // Seed the hero YTD card from a build-time snapshot so the initial HTML
  // never renders "AED 0.00B" while /api/market/monthly-2026 is in flight.
  // The snapshot is refreshed weekly by the Monday DLD ETL cron. The live
  // fetch below silently swaps in fresher values when they arrive.
  // See /app/frontend/src/data/ytd_snapshot.json.
  const [hero2026, setHero2026] = useState(() => ytdSnapshot && ytdSnapshot.ytd ? { ytd: ytdSnapshot.ytd, months: [] } : null);
  // "Fresh" badge only once the live dataset has actually loaded (never on the seed).
  const [liveLoaded, setLiveLoaded] = useState(false);
  const [liveFailed, setLiveFailed] = useState(false);

  // Variant-aware hero copy, falls back to default i18n string when the
  // visitor didn't arrive from a mapped ad campaign.
  const heroCopy = variant?.[lang] || {};
  const heroKicker = heroCopy.kicker || t("hero.kicker");
  const heroBody = heroCopy.body || (isRTL
    ? "وجدتم عقاراً في دبي؟ اعرفوا تكلفته الحقيقية ودخله وعائده المتوقّع، اعتماداً على بيانات دائرة الأراضي والأملاك الرسمية. أدوات مجانية، دون تسجيل، ودون اتصالات تسويقية."
    : "Found a property in Dubai? See what it could really cost, earn and return, using official DLD market data. Free tools. No registration. No cold calls.");
  // Ad-campaign visitors keep their variant CTA (consultation); everyone else starts with the Deal Analyzer.
  const hasVariantCta = Boolean(heroCopy.cta_primary);
  const heroCtaPrimary = hasVariantCta ? heroCopy.cta_primary : (isRTL ? "حلّل عقاراً" : "Analyze a property");
  const ytd = hero2026?.ytd;
  const refreshedOn = ((hero2026?.as_of || "").match(/\d{4}-\d{2}-\d{2}/) || [])[0];

  // Rotating italic head-word, cycles every 8s through 3 on-brand adjectives
  // (Simplified → Sourced → Strategic) while "Secured." stays as the anchor.
  const rotateWords = t("hero.rotate_words") || (isRTL ? ["قبل شراء"] : ["Before you buy"]);
  const [rotateIdx, setRotateIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setRotateIdx((i) => (i + 1) % (rotateWords.length || 1));
    }, 3800);
    return () => clearInterval(id);
  }, [rotateWords.length]);

  // Cursor spotlight, updates CSS vars on the hero DLD card so a soft gold
  // radial-gradient follows the cursor. Purely presentational.
  const handleCardMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  useEffect(() => {
    // Pull the freshest DLD monthly print for the hero live ticker.
    api.get("/market/monthly-2026")
      .then((r) => {
        setHero2026(r.data || null);
        setLiveLoaded(Boolean(r.data?.ytd));
        const months = r.data?.months || [];
        if (months.length) setLatestPrint(months[months.length - 1]);
      })
      .catch(() => { setLiveFailed(true); });
  }, []);

  return (
    <>
      <Helmet>
        <title>{isRTL ? "ذكاء عقاري لدبي وخدمات استشارية | بروبيرتك" : "Dubai Real Estate Intelligence & Advisory | Propertic"}</title>
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="description" content={isRTL
          ? "استكشف بيانات سوق دبي العقاري مباشرةً، قارن المناطق، حلّل الصفقات، احسب الرهون، واحصل على إرشاد مبنيّ على الأدلّة من بروبيرتك بروفست."
          : "Explore live Dubai property data, compare areas, analyze deals, calculate mortgages and get evidence-based guidance from Propertic by Provest."} />
        <link rel="canonical" href={isRTL ? "https://thepropertic.ae/?lang=ar" : "https://thepropertic.ae/"} />
        <link rel="alternate" hrefLang="en" href="https://thepropertic.ae/" />
        <link rel="alternate" hrefLang="ar" href="https://thepropertic.ae/?lang=ar" />
        <link rel="alternate" hrefLang="x-default" href="https://thepropertic.ae/" />
        <meta property="og:title" content={isRTL ? "ذكاء عقاري لدبي وخدمات استشارية | بروبيرتك" : "Dubai Real Estate Intelligence & Advisory | Propertic"} />
        <meta property="og:url" content={isRTL ? "https://thepropertic.ae/?lang=ar" : "https://thepropertic.ae/"} />
        <meta property="og:description" content={isRTL
          ? "استكشف بيانات سوق دبي العقاري مباشرةً، قارن المناطق، حلّل الصفقات، احسب الرهون، واحصل على إرشاد مبنيّ على الأدلّة من بروبيرتك بروفست."
          : "Explore live Dubai property data, compare areas, analyze deals, calculate mortgages and get evidence-based guidance from Propertic by Provest."} />
        <meta name="twitter:title" content={isRTL ? "ذكاء عقاري لدبي وخدمات استشارية | بروبيرتك" : "Dubai Real Estate Intelligence & Advisory | Propertic"} />
        <meta name="twitter:description" content={isRTL
          ? "استكشف بيانات سوق دبي مباشرةً، قارن المناطق، حلّل الصفقات، احسب الرهون. إرشاد مبنيّ على الأدلّة من بروبيرتك بروفست."
          : "Live Dubai property data, area comparison, deal analysis, mortgage tools and evidence-based guidance from Propertic by Provest."} />
      </Helmet>
      {/* ============ HERO split editorial layout ============ */}
      <section className="relative overflow-hidden">
        <div className="blob-gold w-[600px] h-[600px] -top-40 -start-40" />
        <div className="blob-navy w-[500px] h-[500px] -bottom-40 -end-40" />

        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10 pt-4 sm:pt-8 lg:pt-12 pb-10 sm:pb-14 lg:pb-16 relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 relative min-w-0">
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-mono-brand text-propertic-gold mb-3 sm:mb-6"
                data-testid="hero-kicker"
                data-variant={variantId || "default"}
              >
                {heroKicker}
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.05 }}
                className="font-editorial text-[clamp(1.5rem,4.2vw,2.5rem)] leading-[1.15] tracking-[-0.02em] text-propertic-ink"
              >
                {isRTL ? (
                  <>
                    اعرف الأرقام.{" "}
                    <span className="font-italic-serif text-propertic-gold">قبل شراء</span>{" "}
                    عقارك في دبي.
                  </>
                ) : (
                  <>
                    Know the numbers.{" "}
                    <span className="font-italic-serif text-propertic-gold">Before you buy</span>{" "}
                    Dubai property.
                  </>
                )}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
                className="mt-4 sm:mt-8 text-propertic-ink-2/95 text-[14px] sm:text-lg leading-[1.55] sm:leading-[1.65] max-w-xl sm:max-w-2xl"
                data-testid="hero-body"
              >
                {heroBody}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 }}
                className="mt-6 sm:mt-12 flex items-center gap-2 sm:gap-4 flex-wrap"
              >
                <a
                  href={hasVariantCta ? "#consultation" : lh("/property-deal-analyzer")}
                  data-testid="hero-primary-cta"
                  className="btn-primary inline-flex items-center justify-center gap-2 h-11 sm:h-14 px-4 sm:px-8 rounded-full font-mono-brand text-[10px] sm:text-xs tracking-[0.14em] whitespace-nowrap"
                >
                  {heroCtaPrimary}
                  <ArrowRight className={`w-3.5 sm:w-4 h-3.5 sm:h-4 ${isRTL ? "rtl-mirror" : ""}`} />
                </a>
                <a
                  href={lh("/dubai-market")}
                  data-testid="hero-secondary-cta"
                  className="btn-ghost inline-flex items-center justify-center gap-2 h-11 sm:h-14 px-4 sm:px-8 rounded-full font-mono-brand text-[10px] sm:text-xs tracking-[0.14em] whitespace-nowrap"
                >
                  {isRTL ? "استكشف سوق دبي" : "Explore Dubai market"}
                  <ArrowRight className={`w-3.5 sm:w-4 h-3.5 sm:h-4 ${isRTL ? "rtl-mirror" : ""}`} />
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="mt-6 sm:mt-10 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-8 sm:gap-y-3 text-xs text-propertic-muted"
                data-testid="hero-trust-marks"
              >
                <TrustMark label={isRTL ? "بيانات رسمية من دائرة الأراضي" : "Official DLD source data"} />
                <TrustMark label={isRTL ? "أدوات مجانية بالكامل" : "100% free tools"} />
                <TrustMark label={isRTL ? "استشارة مرخّصة اختيارية" : "Optional licensed advisory"} />
              </motion.div>
            </div>

            {/* Example deal card: illustrative values, leads into the real Deal Analyzer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="lg:col-span-5 relative min-w-0"
            >
              <div
                onMouseMove={handleCardMouseMove}
                data-testid="hero-example-deal-card"
                className="spotlight-card w-full text-start rounded-[2rem] p-6 sm:p-7 relative overflow-hidden bg-gradient-to-br from-propertic-navy via-propertic-navy to-[#12213a] text-propertic-paper border border-propertic-gold/25 shadow-[0_20px_50px_rgba(10,37,64,0.18)]"
              >
                <div className="absolute -top-16 -end-16 w-56 h-56 rounded-full bg-propertic-gold/12 blur-3xl pointer-events-none" />
                <div className="relative">
                  <p className="font-mono-brand text-propertic-gold-2 text-[10px] tracking-[0.24em] uppercase">{isRTL ? "طريقة أذكى للشراء" : "A smarter way to buy"}</p>
                  <h2 className="mt-3 font-editorial text-xl sm:text-2xl leading-[1.15] text-propertic-paper">
                    {isRTL ? <>هل هذا العقار <span className="font-italic-serif text-propertic-gold-2">صفقة جيّدة؟</span></> : <>Is this property a <span className="font-italic-serif text-propertic-gold-2">good deal?</span></>}
                  </h2>
                  <dl className="mt-4 pt-4 border-t border-propertic-paper/12 space-y-2 text-sm">
                    {[
                      [isRTL ? "سعر العقار" : "Your property price", isRTL ? `${arIndic("1,500,000")} درهم` : "AED 1,500,000"],
                      [isRTL ? "الإيجار السنوي المتوقّع" : "Expected annual rent", isRTL ? `${arIndic("105,000")} درهم` : "AED 105,000"],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between gap-3">
                        <dt className="text-propertic-paper/65">{k}</dt>
                        <dd className="font-editorial text-propertic-paper tabular force-ltr">{v}</dd>
                      </div>
                    ))}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-propertic-paper/12">
                      <dt className="text-propertic-paper/65">{isRTL ? "العائد الإجمالي التقديري" : "Estimated gross yield"}</dt>
                      <dd className="font-editorial text-2xl text-propertic-gold-2 tabular force-ltr">{isRTL ? "٧٫٠٪" : "7.0%"}</dd>
                    </div>
                  </dl>
                  <p className="mt-4 font-editorial text-base text-propertic-paper">{isRTL ? "انظر أبعد من سعر الإعلان." : "See beyond the listing price."}</p>
                  <p className="mt-1 text-[13px] text-propertic-paper/70 leading-relaxed">{isRTL ? "افهم التكاليف والعائد والمفاضلات الأساسية قبل أن تلتزم." : "Understand costs, yield and key trade-offs before committing."}</p>
                  <a
                    href={lh("/property-deal-analyzer")}
                    data-testid="hero-example-analyze-cta"
                    className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-propertic-gold text-propertic-navy hover:bg-propertic-gold/90 text-xs font-mono-brand tracking-[0.14em] uppercase transition-colors"
                  >
                    {isRTL ? "حلّل عقاراً" : "Analyze a property"}
                    <ArrowRight className={`w-4 h-4 ${isRTL ? "rtl-mirror" : ""}`} />
                  </a>
                  <p className="mt-3 text-[10px] text-propertic-paper/45 font-mono-brand tracking-[0.12em]">{isRTL ? "قيم توضيحية للمثال فقط." : "Illustrative example values."}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Where to start: three practical pathways (replaces the separate Deal Analyzer + Compare CTA bands) */}
      <section className="px-4 sm:px-8 lg:px-16 py-10 sm:py-14" data-testid="home-pathways">
        <div className="mx-auto max-w-[1280px]">
          <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3">{isRTL ? "من أين تبدأ؟" : "Where would you like to start?"}</p>
          <h2 className="font-editorial text-[22px] sm:text-3xl leading-[1.15] text-propertic-ink">
            {isRTL ? <>سؤال واحد، <span className="font-italic-serif text-propertic-gold">وأداة تجيب عنه.</span></> : <>One question. <span className="font-italic-serif text-propertic-gold">One useful tool.</span></>}
          </h2>
          <p className="mt-3 text-propertic-ink-2/85 text-[14px] sm:text-[15px] max-w-2xl">{isRTL ? "لا تحتاج إلى فهم كل إحصاءات السوق لتتّخذ قراراً عقارياً أذكى." : "You don't have to understand every market statistic to make a smarter property decision."}</p>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            <PathCard n="01" testid="home-path-analyzer" href={lh("/property-deal-analyzer")} isRTL={isRTL}
              title={isRTL ? "هل يستحق هذا العقار سعره؟" : "Is this property worth it?"}
              body={isRTL ? "احسب تكاليف الشراء والتدفّق النقدي وعائد الإيجار والعائد التقديري على الاستثمار قبل أن تشتري." : "Calculate acquisition costs, cash flow, rental yield and estimated ROI before you buy."}
              cta={isRTL ? "افتح محلّل الصفقات" : "Open Deal Analyzer"} />
            <PathCard n="02" testid="home-path-market" href={lh("/dubai-market")} isRTL={isRTL}
              title={isRTL ? "أين يجب أن أبحث؟" : "Where should I look?"}
              body={isRTL ? "استكشف مناطق دبي ونشاط السوق الرسمي دون أن تضيع بين الأرقام." : "Explore Dubai areas and official market activity without getting lost in numbers."}
              cta={isRTL ? "استكشف المناطق" : "Explore areas"}
              extra={<a href={lh("/dubai-compare-areas")} data-testid="home-path-compare" className="text-propertic-gold hover:underline">{isRTL ? "أو قارن بين منطقتين" : "or compare two areas"}</a>} />
            <PathCard n="03" testid="home-path-roni" isRTL={isRTL} onClick={() => window.dispatchEvent(new CustomEvent("roni:open"))}
              title={isRTL ? "ماذا يجب أن أسأل؟" : "What should I ask?"}
              body={isRTL ? "اسأل روني عن عقارات دبي، مع سياق البيانات الرسمية والمصادر عند توفّرها." : "Ask Roni about Dubai real estate, with official data context and sources where available."}
              cta={isRTL ? "تحدّث مع روني" : "Meet Roni"} />
          </div>
        </div>
      </section>

      {/* Market snapshot: 3 headline figures; full detail stays in the DOM inside <details> */}
      <section className="px-4 sm:px-8 lg:px-16 pb-8 sm:pb-12" data-testid="home-market-snapshot">
        <div className="mx-auto max-w-[1280px] rounded-[2rem] bg-propertic-sand/60 border hairline px-6 sm:px-10 py-8 sm:py-10">
          <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3">{isRTL ? "السوق دون تعقيد" : "The market, without the overwhelm"}</p>
          <h2 className="font-editorial text-[22px] sm:text-3xl leading-[1.15] text-propertic-ink">{isRTL ? "لمحة سريعة عن سوق دبي." : "A quick Dubai market snapshot."}</h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              { k: isRTL ? "٢٠٢٦ حتى اليوم · قيمة المبيعات الفعلية" : "2026 YTD · Pure sales value", v: ytd?.sales_value_aed_bn != null ? (isRTL ? `${arIndic(ytd.sales_value_aed_bn.toFixed(2))} مليار درهم` : `AED ${ytd.sales_value_aed_bn.toFixed(2)}B`) : "—", m: isRTL ? "مبيعات مسجّلة فقط، وليس كل الإجراءات." : "Recorded property sales, not all procedures.", id: "snapshot-sales-value" },
              { k: isRTL ? "٢٠٢٦ حتى اليوم · عدد المبيعات" : "2026 YTD · Pure sales", v: ytd?.sales_transactions != null ? (isRTL ? arIndic(new Intl.NumberFormat("en-US").format(ytd.sales_transactions)) : new Intl.NumberFormat("en-US").format(ytd.sales_transactions)) : "—", m: isRTL ? "صفقات بيع مكتملة." : "Completed sales transactions.", id: "snapshot-sales-count" },
              { k: isRTL ? "المبيعات · الوسيط لسعر القدم²" : "Sales · Median price / sqft", v: ytd?.median_aed_per_sqft ? (isRTL ? `${arIndic(new Intl.NumberFormat("en-US").format(Math.round(ytd.median_aed_per_sqft)))} درهم` : `AED ${new Intl.NumberFormat("en-US").format(Math.round(ytd.median_aed_per_sqft))}`) : "—", m: isRTL ? "الوسيط على مستوى دبي." : "Dubai-wide median.", id: "snapshot-median-sqft" },
            ].map((s) => (
              <div key={s.id} className="rounded-2xl bg-white/80 border hairline p-5" data-testid={s.id}>
                <p className="font-mono-brand text-[10px] tracking-[0.16em] uppercase text-propertic-muted mb-2">{s.k}</p>
                <p className="font-editorial text-3xl text-propertic-navy tabular force-ltr">{s.v}</p>
                <p className="mt-2 text-[12px] text-propertic-ink-2/80">{s.m}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-[11px] font-mono-brand text-propertic-muted tracking-[0.08em] leading-relaxed" data-testid="home-dld-attribution">
            {isRTL
              ? `المصدر: دائرة الأراضي والأملاك في دبي (DLD)، سجلّات الصفقات الرسمية. تُحدَّث أسبوعياً كل يوم اثنين، بحسب توفّر النشر.${refreshedOn ? ` آخر تحديث: ${arIndic(refreshedOn)}.` : ""}`
              : `Source: Dubai Land Department (DLD), official transaction records. Updated weekly on Monday, subject to publication availability.${refreshedOn ? ` Last refreshed: ${refreshedOn}.` : ""}`}
          </p>
          <details className="mt-6 group" data-testid="home-market-details">
            <summary className="cursor-pointer list-none inline-flex items-center gap-2 h-11 px-6 rounded-full border border-propertic-navy/25 hover:border-propertic-navy bg-white/60 text-propertic-navy text-xs font-mono-brand tracking-[0.14em] uppercase transition-colors" data-testid="home-market-details-toggle">
              {isRTL ? "عرض التفاصيل الكاملة للسوق" : "See the full market breakdown"}
              <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
            </summary>
            <div className="mt-6 max-w-xl">
              <button
                type="button"
                onClick={() => setMarket2026Open(true)}
                onMouseMove={handleCardMouseMove}
                data-testid="hero-stat-open-2026"
                className="spotlight-card group w-full text-start rounded-[2rem] p-8 relative overflow-hidden transition-all bg-gradient-to-br from-propertic-navy via-propertic-navy to-[#12213a] text-propertic-paper border border-propertic-gold/25 hover:border-propertic-gold/60 shadow-[0_20px_50px_rgba(10,37,64,0.18)]"
              >
                <div className="absolute -top-16 -end-16 w-56 h-56 rounded-full bg-propertic-gold/12 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="font-mono-brand text-propertic-gold-2 text-[10px] tracking-[0.24em] uppercase">{isRTL ? "دائرة الأراضي · تحديث أسبوعي" : "DLD · Weekly refresh"}</p>
                    <div className="flex items-center gap-2">
                      <DubaiClock isRTL={isRTL} label={t("hero.dubai_clock_label")} />
                      {liveLoaded && (
                      <span className="inline-flex items-center gap-1.5 font-mono-brand text-emerald-300 text-[10px] tracking-[0.2em] uppercase" data-testid="hero-fresh-badge">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        {isRTL ? "محدَّث" : "Fresh"}
                      </span>
                      )}
                      {liveFailed && (
                      <span className="font-mono-brand text-amber-300 text-[10px] tracking-[0.14em] uppercase" data-testid="hero-stale-badge">
                        {isRTL ? `لقطة سابقة${ytdSnapshot?.last_refresh_utc ? ` · ${ytdSnapshot.last_refresh_utc.slice(0, 10)}` : ""}` : `Earlier snapshot${ytdSnapshot?.last_refresh_utc ? ` · ${ytdSnapshot.last_refresh_utc.slice(0, 10)}` : ""}`}
                      </span>
                      )}
                    </div>
                  </div>
                  {/* KEY HEADLINE = PURE SALES value (what buyers care about) */}
                  <p className="font-mono-brand text-propertic-paper/55 mt-5 text-[10px] tracking-[0.22em] uppercase">{isRTL ? "دبي · مبيعات ٢٠٢٦ حتى اليوم" : "Dubai · 2026 sales year-to-date"}</p>
                  <p className="mt-2 font-editorial text-4xl lg:text-5xl text-propertic-gold-2 tabular force-ltr leading-[0.95]" dir="ltr" aria-label={(() => { const bn = hero2026?.ytd?.sales_value_aed_bn || 0; return isRTL ? `${arIndic(bn.toFixed(2))} مليار درهم مبيعات دبي منذ بداية ٢٠٢٦` : `AED ${bn.toFixed(2)} billion Dubai sales year-to-date 2026`; })()}>
                    {(() => {
                      const target = ((hero2026?.ytd?.sales_value_aed_bn) || 0) * 1e9;
                      const seed = ((ytdSnapshot?.ytd?.sales_value_aed_bn) || 0) * 1e9;
                      return isRTL
                        ? <AnimatedAED targetAed={target} initialAed={seed} format={(v) => `${arIndic((v / 1e9).toFixed(2))} مليار درهم`} />
                        : <AnimatedAED targetAed={target} initialAed={seed} format={(v) => format(v, { compact: true, decimals: 2 })} />;
                    })()}
                  </p>
                  <p className="mt-2 text-propertic-paper/75 text-sm">
                    {isRTL
                      ? `${arIndic(new Intl.NumberFormat("en-US").format(hero2026?.ytd?.sales_transactions || 0))} صفقة بيع فعلية`
                      : `${new Intl.NumberFormat("en-US").format(hero2026?.ytd?.sales_transactions || 0)} pure sales transactions`}
                  </p>

                  <div className="mt-6 pt-5 border-t border-propertic-paper/12 grid grid-cols-2 gap-4">
                    {(() => {
                      const offPct = hero2026?.ytd?.off_plan_share_pct || 0;
                      const readyPct = hero2026?.ytd?.ready_share_pct || 0;
                      const offV = hero2026?.ytd?.off_plan_value_aed_bn;
                      const readyV = hero2026?.ytd?.ready_value_aed_bn;
                      // R2-MKT-01: value from actual transaction values, never count share × total.
                      const offBn = offV != null ? offV.toFixed(2) : "—";
                      const readyBn = readyV != null ? readyV.toFixed(2) : "—";
                      const arDigit = (s) => arIndic(String(s));
                      return (
                        <>
                          <div>
                            <p className="font-mono-brand text-propertic-paper/50 text-[10px] tracking-[0.2em] uppercase">{isRTL ? "قيد الإنشاء" : "Off-plan"}</p>
                            <p className="mt-1.5 font-editorial text-2xl text-propertic-paper tabular leading-none"><span className="force-ltr">{isRTL ? `${arDigit(offPct)}٪` : `${offPct}%`}</span></p>
                            <p className="text-[9px] text-propertic-paper/45 font-mono-brand tracking-[0.12em] uppercase mt-1">{isRTL ? "حصة المبيعات" : "share of sales"}</p>
                            <p className="text-[11px] text-propertic-paper/55 mt-1.5">{isRTL ? `${arDigit(offBn)} مليار درهم` : `AED ${offBn}B`}</p>
                          </div>
                          <div>
                            <p className="font-mono-brand text-propertic-paper/50 text-[10px] tracking-[0.2em] uppercase">{isRTL ? "جاهزة / ثانوية" : "Ready / secondary"}</p>
                            <p className="mt-1.5 font-editorial text-2xl text-propertic-paper tabular leading-none"><span className="force-ltr">{isRTL ? `${arDigit(readyPct)}٪` : `${readyPct}%`}</span></p>
                            <p className="text-[9px] text-propertic-paper/45 font-mono-brand tracking-[0.12em] uppercase mt-1">{isRTL ? "حصة المبيعات" : "share of sales"}</p>
                            <p className="text-[11px] text-propertic-paper/55 mt-1.5">{isRTL ? `${arDigit(readyBn)} مليار درهم` : `AED ${readyBn}B`}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="mt-4 pt-4 border-t border-propertic-paper/12 flex items-center justify-between gap-2 text-[11px] text-propertic-paper/55">
                    <span>{isRTL ? "إجمالي الإجراءات (شامل الرهون والهبات)" : "Total procedures (incl. mortgages + gifts)"}</span>
                    <span className="font-editorial text-propertic-paper text-sm tabular">
                      {isRTL
                        ? `${arIndic((hero2026?.ytd?.volume_aed_bn || 0).toFixed(2))} مليار درهم`
                        : `AED ${(hero2026?.ytd?.volume_aed_bn || 0).toFixed(2)}B`}
                    </span>
                  </div>

                  {latestPrint && (
                    <div className="mt-4 pt-4 border-t border-propertic-paper/12 flex items-center gap-3 flex-wrap" data-testid="hero-live-ticker">
                      <span className="inline-flex items-center gap-1.5 font-mono-brand text-emerald-300 text-[9px] sm:text-[10px] tracking-[0.22em] uppercase">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        {isRTL ? "أحدث شهر" : "Latest month"}
                      </span>
                      <span className="font-editorial text-propertic-paper text-[13px] sm:text-sm tabular">
                        {isRTL
                          ? `${arIndic(latestPrint.month)} · ${arIndic((latestPrint.sales_aed_bn || latestPrint.volume_aed_bn || 0).toFixed(2))} مليار درهم${latestPrint.sales_transactions ? ` · ${arIndic(new Intl.NumberFormat("en-US").format(latestPrint.sales_transactions))} صفقة بيع` : ""}`
                          : `${latestPrint.month}${latestPrint.sales_aed_bn ? ` · AED ${latestPrint.sales_aed_bn}B sales` : ` · AED ${latestPrint.volume_aed_bn}B`}${latestPrint.sales_transactions ? ` · ${new Intl.NumberFormat("en-US").format(latestPrint.sales_transactions)} sales` : ""}`}
                      </span>
                    </div>
                  )}

                  <div className="mt-6 flex items-center justify-between text-propertic-gold-2/80 group-hover:text-propertic-gold-2 transition-colors">
                    <span className="font-mono-brand text-[10px] tracking-[0.2em] uppercase">{isRTL ? "افتح تفاصيل الأشهر" : "Open month-by-month"}</span>
                    <ChevronRight className={`w-4 h-4 ${isRTL ? "rtl-mirror" : ""} transition-transform group-hover:translate-x-1`} />
                  </div>
                </div>
              </button>
              <p className="mt-3 text-[10px] font-mono-brand text-propertic-muted tracking-[0.18em] text-center uppercase leading-relaxed" data-testid="hero-source-ribbon">
                {isRTL
                  ? "المصدر: دائرة الأراضي · بيع فعلي فقط · يُحدَّث كل أسبوع"
                  : "Source · Dubai Land Department · Pure sales only · Refreshed weekly"}
              </p>
              <p className="mt-1.5 text-[10px] font-mono-brand text-propertic-muted/80 tracking-[0.14em] text-center">
                {isRTL
                  ? (<>الرقم أعلاه = صفقات بيع فقط. لعرض كل الإجراءات (بيع + رهون + هبات) طالع{" "}
                    <a href={lh("/insights")} className="text-propertic-gold hover:underline">التحليلات الأسبوعية</a>.</>)
                  : (<>Number above = sales transactions only. For all procedures (sales + mortgages + gifts) see{" "}
                    <a href={lh("/insights")} className="text-propertic-gold hover:underline">Weekly Insights</a>.</>)}
              </p>
            </div>
          </details>
        </div>
      </section>

      <ConsultationForm copy={isRTL ? {
        eyebrow: "مساعدة بشرية · عندما تريدها فقط",
        title_pre: "شخص حقيقي،",
        title_post: "عندما تحتاج إليه.",
        subtitle: "استخدم كل الأدوات بحرّية. وعندما تريد رأياً ثانياً من خبير، اطلب محادثة مجانية مع فريق بروفست الاستشاري المرخّص.",
        bullet_1: "اختيارية وليست إلزامية. لا مكالمات إلا إذا طلبتها.",
        bullet_2: "ابدأ بأرقامك: ناقش عقاراً حقيقياً أو ميزانيتك أو أهدافك.",
        bullet_3: "استشارة محلية مرخّصة: بروفست العقارية ذ.م.م · رقم ريرا ٤٣٩٥٠",
      } : {
        eyebrow: "Human help · only when you want it",
        title_pre: "A real person,",
        title_post: "when you want one.",
        subtitle: "Use every tool freely. When you're ready for an expert second opinion, request a free conversation with Provest's licensed advisory team.",
        bullet_1: "Optional, not obligatory. No calls unless you ask for one.",
        bullet_2: "Start with your numbers. Discuss a real property, a budget or your goals.",
        bullet_3: "Licensed local advisory. Provest Real Estate LLC · RERA ORN 43950",
      }} />
      <HandoverCollection />

      {/* ============ THREE PILLARS (Our promise), kept for the bottom of the page, just before the FAQ, as the closing reassurance ============ */}
      <section className="relative py-12 sm:py-16 lg:py-20 bg-propertic-navy text-propertic-paper overflow-hidden">
        <div className="blob-gold w-[500px] h-[500px] -top-40 -end-40 opacity-30" />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-8 sm:mb-10">
            <div className="max-w-xl">
              <p className="font-mono-brand text-propertic-gold-2 mb-3 sm:mb-4 text-[10px] tracking-[0.14em]">{isRTL ? "وعدنا" : "Our promise"}</p>
              <h2 className="font-editorial text-[22px] sm:text-3xl leading-[1.15] text-propertic-paper">
                {isRTL ? (
                  <>ثلاثة أشياء نلتزم بها <span className="font-italic-serif text-propertic-gold-2">دائماً</span>.</>
                ) : (
                  <>Three things we always <span className="font-italic-serif text-propertic-gold-2">deliver</span>.</>
                )}
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5 lg:gap-8">
            {[
              { icon: Key, key: 1 },
              { icon: ShieldCheck, key: 2 },
              { icon: TrendingUp, key: 3 },
            ].map((p, i) => (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: 0.05 * i }}
                className="rounded-[1.75rem] p-6 sm:p-8 lg:p-10 relative overflow-hidden bg-propertic-navy-2/40 border border-propertic-paper/10 hover:border-propertic-gold/50 transition-colors"
                data-testid={`pillar-${p.key}`}
              >
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-propertic-gold/15 border border-propertic-gold/25 grid place-items-center text-propertic-gold-2 mb-5 sm:mb-7">
                  <p.icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <h3 className="font-editorial text-lg text-propertic-paper mb-2 sm:mb-3">{t(`hero.pillar_${p.key}_title`)}</h3>
                <p className="text-[14px] sm:text-[15px] text-propertic-paper/70 leading-relaxed">{t(`hero.pillar_${p.key}_body`)}</p>
                <div className="mt-5 sm:mt-7 pt-4 sm:pt-5 border-t border-propertic-paper/10 flex items-center gap-2 font-mono-brand text-propertic-gold-2 text-[10px] tracking-[0.14em]">
                  <span className="tabular">0{p.key}</span>
                  <span className="text-propertic-paper/40">/03</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency strip — moved directly above FAQ (Sept 2026) so the
          "raw DLD vs. Propertic opinion" disclosure is the final piece of
          context the user reads before the FAQ, right after the closing
          promise pillars. Keeps the mid-page flow (Deal Analyzer → Market
          → Compare → Consultation → Handover → Pillars) uninterrupted. */}
      <section className="py-6 sm:py-8 px-4 sm:px-8 lg:px-16 bg-propertic-sand/50 border-y hairline" data-testid="home-transparency-strip">
        <div className="mx-auto max-w-[1400px] grid md:grid-cols-2 gap-4 sm:gap-6">
          <div className="rounded-2xl bg-white/80 border hairline p-4 sm:p-5">
            <p className="font-mono-brand text-emerald-700 text-[10px] tracking-[0.22em] uppercase mb-2 inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
              {isRTL ? "بيانات مستقلة · دائرة الأراضي" : "Independent data · DLD"}
            </p>
            <p className="text-[13px] text-propertic-ink-2/95 leading-relaxed">
              {isRTL
                ? "السجلات الأساسية للصفقات (عدد الصفقات، القيم، الأسعار) مأخوذة مباشرة من بوابة البيانات المفتوحة لدائرة الأراضي والأملاك في دبي. أما المؤشرات المحسوبة (العائد، الحصص، التغيّر السنوي) فتُحتسب من قِبل بروبيرتك وفق المنهجية الموضحة بجانب كل مؤشر."
                : "Underlying transaction records (counts, values, prices) come directly from the Dubai Land Department Open Data Gateway. Derived metrics (yield, share%, YoY change) are calculated by The Propertic using the methodology shown beside each metric."}
            </p>
          </div>
          <div className="rounded-2xl bg-white/80 border hairline p-4 sm:p-5">
            <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.22em] uppercase mb-2 inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-propertic-gold" aria-hidden="true" />
              {isRTL ? "رأي استشاري · بروبيرتك (ريرا #٤٣٩٥٠)" : "Advisory opinion · Propertic (RERA #43950)"}
            </p>
            <p className="text-[13px] text-propertic-ink-2/95 leading-relaxed">
              {isRTL
                ? "أي وصف بأن منطقة أو مشروع «جذّاب» أو «قابل للاستثمار» هو رأي استشاري من فريق بروفست العقاري، وليس بياناً من الدائرة. نحن وسيط مرخّص وقد نستفيد تجارياً من صفقتك."
                : "Any statement that an area or project is \"attractive\" or \"investable\" is an advisory opinion from the Provest team, not a DLD statement. We are a licensed broker and may commercially benefit from your transaction."}
            </p>
          </div>
        </div>
      </section>

      <FAQ />
      <LoanForm ref={loanRef} />
      <Market2026Dialog open={market2026Open} onClose={() => setMarket2026Open(false)} />
    </>
  );
}

function PathCard({ n, title, body, cta, href, onClick, extra, testid, isRTL }) {
  const cls = "inline-flex items-center gap-2 text-propertic-navy font-mono-brand text-xs tracking-[0.14em] uppercase hover:text-propertic-gold transition-colors";
  const arrow = <ArrowRight className={`w-4 h-4 ${isRTL ? "rtl-mirror" : ""}`} />;
  return (
    <div className="rounded-[1.75rem] bg-white/80 border hairline hover:border-propertic-gold/50 p-6 sm:p-7 flex flex-col transition-colors" data-testid={testid}>
      <span className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.2em]">{n}</span>
      <h3 className="mt-3 font-editorial text-xl text-propertic-ink">{title}</h3>
      <p className="mt-2 text-[14px] text-propertic-ink-2/85 leading-relaxed flex-1">{body}</p>
      <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px]">
        {href
          ? <a href={href} className={cls} data-testid={`${testid}-cta`}>{cta}{arrow}</a>
          : <button type="button" onClick={onClick} className={cls} data-testid={`${testid}-cta`}>{cta}{arrow}</button>}
        {extra}
      </div>
    </div>
  );
}

function TrustMark({ label }) {
  return (
    <span className="flex flex-col items-center justify-center gap-1.5 text-center rounded-xl border border-propertic-hairline-2 bg-white/60 px-1.5 py-2.5 text-[9.5px] leading-tight tracking-[0.06em] font-mono-brand sm:inline-flex sm:flex-row sm:gap-2.5 sm:border-0 sm:bg-transparent sm:p-0 sm:text-xs sm:tracking-normal sm:text-start">
      <span className="hidden sm:block w-6 h-px bg-propertic-gold" />
      {label}
    </span>
  );
}

/**
 * Ticks the AED headline number up to `targetAed`. Historically animated from
 * 0, which caused an "AED 0.00B" first-paint flash for JS-off crawlers and
 * hard-refresh users. Now accepts `initialAed` (the build-time YTD snapshot
 * value) so the first render already shows a real number. Animation runs
 * only when `targetAed` diverges from `initialAed` after the live fetch
 * lands (typical case: same value → no animation, no flash).
 */
function AnimatedAED({ targetAed, format, initialAed = 0 }) {
  const [value, setValue] = useState(initialAed);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (started || !ref.current) return;
    // Seed present: jump straight to the live value. A count-up from the seed
    // let page snapshots capture mid-animation numbers (e.g. 383.64B vs 383.73B).
    if (initialAed > 0) {
      setValue(targetAed);
      setStarted(true);
      return;
    }
    const el = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setStarted(true);
        observer.disconnect();
      }
    }, { threshold: 0.35 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [started, initialAed, targetAed]);
  useEffect(() => {
    if (!started) return;
    const startValue = value;
    const start = performance.now();
    const duration = 1800;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(startValue + (targetAed - startValue) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, targetAed]);
  return <span ref={ref} data-testid="hero-animated-aed">{format(value)}</span>;
}

// Convert 0-9 and "." in a string to Arabic-Indic digits + decimal separator.
const arIndic = (s) =>
  String(s)
    .replace(/[0-9]/g, (d) => "٠١٢٣٤٥٦٧٨٩"[+d])
    .replace(/\./g, "٫");

/**
 * Live Gulf Standard Time (UTC+4) ticker for the hero card. Reassures
 * overseas viewers that our data + advisors are actually in Dubai right now.
 * Updates every 30s.
 */
function DubaiClock({ isRTL, label }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  // Convert to UTC+4 regardless of viewer timezone.
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const dubai = new Date(utc + 4 * 3600000);
  const hh = String(dubai.getHours()).padStart(2, "0");
  const mm = String(dubai.getMinutes()).padStart(2, "0");
  const timeStr = isRTL
    ? `${hh}:${mm}`.replace(/[0-9]/g, (d) => "٠١٢٣٤٥٦٧٨٩"[+d])
    : `${hh}:${mm}`;
  return (
    <span
      data-testid="hero-dubai-clock"
      className="inline-flex items-center gap-1.5 font-mono-brand text-propertic-gold-2/80 text-[10px] tracking-[0.16em] uppercase"
      aria-label={label}
    >
      <Clock className="w-3 h-3" strokeWidth={2} />
      <span className="tabular force-ltr" dir="ltr">{timeStr}</span>
      <span className="opacity-60">UAE</span>
    </span>
  );
}
